import React, { useState } from 'react';

/**
 * Parse a date string in various formats and return YYYY-MM-DD
 * Accepts: MM/DD/YY, MM/DD/YYYY, M/D/YY, M/D/YYYY
 * Assumes American format (month/day/year)
 */
const parseDateInput = (input) => {
  if (!input) return '';

  // If already in YYYY-MM-DD format, return as-is
  if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
    return input;
  }

  // Try to parse MM/DD/YY or MM/DD/YYYY format
  const match = input.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
  if (!match) return input; // Return original if no match

  let [, month, day, year] = match;

  // Convert 2-digit year to 4-digit (assume 2000s for 00-99)
  if (year.length === 2) {
    year = '20' + year;
  }

  // Pad month and day
  month = month.padStart(2, '0');
  day = day.padStart(2, '0');

  return `${year}-${month}-${day}`;
};

/**
 * Format YYYY-MM-DD to MM/DD/YYYY for display
 */
const formatDateForDisplay = (isoDate) => {
  if (!isoDate) return '';
  const match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return isoDate;
  const [, year, month, day] = match;
  return `${month}/${day}/${year}`;
};

const TreatmentPlanReviewForm = ({ onSave, onCancel, existingData = [] }) => {
  // Initialize with existing data or one empty row
  const getInitialData = () => {
    if (existingData && existingData.length > 0) {
      return existingData.map(row => ({
        clientId: row.clientId || '',
        dateOfLastReview: row.dateOfLastReview || '',
        targetReviewInterval: row.targetReviewInterval || ''
      }));
    }
    return [{ clientId: '', dateOfLastReview: '', targetReviewInterval: '' }];
  };

  const [formData, setFormData] = useState(getInitialData());
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchStatus, setSearchStatus] = useState(null); // 'found' | 'notfound'
  const [highlightedRow, setHighlightedRow] = useState(null);
  const rowRefs = React.useRef({});
  const pendingScrollRef = React.useRef(false);

  // Add a single row
  const addRow = () => {
    pendingScrollRef.current = true;
    setFormData([...formData, { clientId: '', dateOfLastReview: '', targetReviewInterval: '' }]);
  };

  // Add multiple rows
  const addMultipleRows = (count) => {
    pendingScrollRef.current = true;
    const newRows = Array(count).fill().map(() => ({
      clientId: '',
      dateOfLastReview: '',
      targetReviewInterval: ''
    }));
    setFormData([...formData, ...newRows]);
  };

  // Auto-scroll to the newly added row(s) after they render
  React.useEffect(() => {
    if (!pendingScrollRef.current) return;
    pendingScrollRef.current = false;
    const el = rowRefs.current[formData.length - 1];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [formData.length]);

  // Remove a row
  const removeRow = (index) => {
    if (formData.length > 1) {
      const newData = formData.filter((_, i) => i !== index);
      setFormData(newData);

      // Clear validation errors for removed row and reindex
      const newErrors = {};
      Object.keys(validationErrors).forEach(key => {
        const [rowIndex, field] = key.split('.');
        const oldIndex = parseInt(rowIndex);
        if (oldIndex < index) {
          newErrors[key] = validationErrors[key];
        } else if (oldIndex > index) {
          newErrors[`${oldIndex - 1}.${field}`] = validationErrors[key];
        }
      });
      setValidationErrors(newErrors);
    }
  };

  // Search for a client by ID and scroll to their row
  const handleSearch = () => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return;

    const matchIndex = formData.findIndex(row =>
      row.clientId.trim().toLowerCase() === query
    );

    if (matchIndex !== -1) {
      setSearchStatus('found');
      setHighlightedRow(matchIndex);
      rowRefs.current[matchIndex]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      setSearchStatus('notfound');
      setTimeout(() => setSearchStatus(null), 2500);
    }
  };

  // Update a field value
  const updateField = (index, field, value) => {
    const newData = [...formData];
    newData[index] = { ...newData[index], [field]: value };
    setFormData(newData);

    // Clear validation error for this field
    const errorKey = `${index}.${field}`;
    if (validationErrors[errorKey]) {
      const newErrors = { ...validationErrors };
      delete newErrors[errorKey];
      setValidationErrors(newErrors);
    }
  };

  // Validate the form
  const validateForm = () => {
    const errors = {};
    let hasErrors = false;
    const today = new Date().toISOString().split('T')[0];

    formData.forEach((row, index) => {
      // Validate Client ID
      if (!row.clientId.trim()) {
        errors[`${index}.clientId`] = 'Client ID is required';
        hasErrors = true;
      }

      // Validate Date of Last Review
      if (!row.dateOfLastReview) {
        errors[`${index}.dateOfLastReview`] = 'Date is required';
        hasErrors = true;
      } else if (row.dateOfLastReview > today) {
        errors[`${index}.dateOfLastReview`] = 'Date cannot be in the future';
        hasErrors = true;
      }

      // Validate Target Review Interval
      const interval = parseInt(row.targetReviewInterval);
      if (!row.targetReviewInterval || isNaN(interval)) {
        errors[`${index}.targetReviewInterval`] = 'Interval is required';
        hasErrors = true;
      } else if (interval < 1 || interval > 365) {
        errors[`${index}.targetReviewInterval`] = 'Must be 1-365 days';
        hasErrors = true;
      }
    });

    // Check for duplicate Client IDs
    const clientIds = formData.map(row => row.clientId.trim().toLowerCase()).filter(id => id);
    const duplicates = clientIds.filter((id, index) => clientIds.indexOf(id) !== index);

    if (duplicates.length > 0) {
      formData.forEach((row, index) => {
        if (duplicates.includes(row.clientId.trim().toLowerCase())) {
          errors[`${index}.clientId`] = 'Duplicate Client ID';
          hasErrors = true;
        }
      });
    }

    setValidationErrors(errors);
    return !hasErrors;
  };

  // Handle save
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Filter out empty rows and format data
      const validData = formData
        .filter(row => row.clientId.trim() && row.dateOfLastReview && row.targetReviewInterval)
        .map(row => ({
          clientId: row.clientId.trim(),
          dateOfLastReview: row.dateOfLastReview,
          targetReviewInterval: parseInt(row.targetReviewInterval)
        }));

      if (validData.length === 0) {
        setValidationErrors({ form: 'At least one complete row is required' });
        setIsSubmitting(false);
        return;
      }

      await onSave(validData);
    } catch (error) {
      console.error('Error saving data:', error);
      setValidationErrors({ form: 'Failed to save data. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Count valid rows
  const validRowCount = formData.filter(
    row => row.clientId.trim() && row.dateOfLastReview && row.targetReviewInterval
  ).length;

  return (
    <div className="bg-white rounded-xl shadow-md">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-100 to-blue-200 rounded-t-xl p-6 border-b border-blue-300">
        <div className="flex items-center justify-center mb-2">
          <div className="w-12 h-12 bg-blue-700 rounded-full flex items-center justify-center mr-4">
            <span className="text-white font-bold text-xl">📋</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Treatment Plan Review Data Entry</h2>
        </div>
        <p className="text-gray-700 text-center">
          Enter client treatment plan review information below
        </p>
      </div>

      {/* Form Content */}
      <div className="p-6">
        {/* Form-level error */}
        {validationErrors.form && (
          <div className="mb-4 p-3 bg-red-50 border border-red-300 rounded-lg text-red-700">
            {validationErrors.form}
          </div>
        )}

        {/* Add Rows Buttons + Search */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <button
            onClick={addRow}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
          >
            + Add Row
          </button>
          <button
            onClick={() => addMultipleRows(5)}
            className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors font-medium"
          >
            + Add 5 Rows
          </button>
          <button
            onClick={() => addMultipleRows(10)}
            className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors font-medium"
          >
            + Add 10 Rows
          </button>

          {/* Search Client ID */}
          <div className="flex items-center gap-1 ml-auto">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setSearchStatus(null); setHighlightedRow(null); }}
                onFocus={() => setHighlightedRow(null)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search Client ID..."
                className={`pl-8 pr-3 py-2 text-sm border rounded-lg w-48 focus:outline-none focus:ring-2 transition-colors ${
                  searchStatus === 'notfound'
                    ? 'border-red-400 bg-red-50 focus:ring-red-300'
                    : searchStatus === 'found'
                    ? 'border-green-400 bg-green-50 focus:ring-green-300'
                    : 'border-gray-300 focus:ring-blue-300'
                }`}
              />
              {searchStatus === 'notfound' && (
                <span className="absolute -bottom-5 left-0 text-xs text-red-600 whitespace-nowrap">No match found</span>
              )}
            </div>
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Go
            </button>
          </div>
        </div>

        {/* Data Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-50">
                <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">
                  Client ID
                </th>
                <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">
                  Date of Last Review
                </th>
                <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">
                  Target Review Interval (Days)
                </th>
                <th className="border border-gray-300 px-4 py-3 text-center font-semibold text-gray-900 w-20">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {formData.map((row, index) => (
                <tr
                  key={index}
                  ref={el => rowRefs.current[index] = el}
                  className={`transition-colors duration-300 ${
                    highlightedRow === index
                      ? 'bg-yellow-100 ring-2 ring-yellow-400'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  {/* Client ID */}
                  <td className="border border-gray-300 px-2 py-2">
                    <input
                      type="text"
                      value={row.clientId}
                      onChange={(e) => updateField(index, 'clientId', e.target.value)}
                      placeholder="Client-001"
                      className={`w-full px-3 py-2 border rounded-md text-sm ${
                        validationErrors[`${index}.clientId`]
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-300'
                      }`}
                    />
                    {validationErrors[`${index}.clientId`] && (
                      <div className="text-red-600 text-xs mt-1">
                        {validationErrors[`${index}.clientId`]}
                      </div>
                    )}
                  </td>

                  {/* Date of Last Review */}
                  <td className="border border-gray-300 px-2 py-2">
                    <input
                      type="text"
                      value={row.dateOfLastReview ? formatDateForDisplay(row.dateOfLastReview) : ''}
                      onChange={(e) => updateField(index, 'dateOfLastReview', e.target.value)}
                      onBlur={(e) => {
                        const parsed = parseDateInput(e.target.value);
                        updateField(index, 'dateOfLastReview', parsed);
                      }}
                      placeholder="MM/DD/YYYY"
                      className={`w-full px-3 py-2 border rounded-md text-sm ${
                        validationErrors[`${index}.dateOfLastReview`]
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-300'
                      }`}
                    />
                    {validationErrors[`${index}.dateOfLastReview`] && (
                      <div className="text-red-600 text-xs mt-1">
                        {validationErrors[`${index}.dateOfLastReview`]}
                      </div>
                    )}
                  </td>

                  {/* Target Review Interval */}
                  <td className="border border-gray-300 px-2 py-2">
                    <input
                      type="number"
                      value={row.targetReviewInterval}
                      onChange={(e) => updateField(index, 'targetReviewInterval', e.target.value)}
                      placeholder="90"
                      min="1"
                      max="365"
                      className={`w-full px-3 py-2 border rounded-md text-sm ${
                        validationErrors[`${index}.targetReviewInterval`]
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-300'
                      }`}
                    />
                    {validationErrors[`${index}.targetReviewInterval`] && (
                      <div className="text-red-600 text-xs mt-1">
                        {validationErrors[`${index}.targetReviewInterval`]}
                      </div>
                    )}
                  </td>

                  {/* Remove Button */}
                  <td className="border border-gray-300 px-2 py-2 text-center">
                    <button
                      onClick={() => removeRow(index)}
                      disabled={formData.length === 1}
                      className={`px-3 py-1 rounded-md text-sm font-medium ${
                        formData.length === 1
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-red-100 text-red-700 hover:bg-red-200'
                      }`}
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer Actions */}
        <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200">
          <span className="text-gray-600">
            {validRowCount} of {formData.length} rows complete
          </span>
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSubmitting || validRowCount === 0}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                isSubmitting || validRowCount === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isSubmitting ? 'Saving...' : `Save Data (${validRowCount} records)`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TreatmentPlanReviewForm;
